<template>
    <div class="bg-white rounded-lgr border border-valence-grey-250 flex flex-col overflow-hidden text-[#595959]">
        <!-- New insights state: styled like MyWorkInsightCard -->
        <template v-if="cardState === 'new_insights' && insightSummary">
            <div class="flex flex-col flex-1 min-h-0 p-5 gap-2">
                <div class="flex gap-5 flex-1 min-h-0">
                    <img :src="InsightGradientBar" alt="" class="w-[9px] shrink-0 self-stretch rounded-full object-cover" />
                    <div class="flex flex-col flex-1 min-h-0 gap-2">
                        <div class="flex items-center gap-1.5 text-valence-grey-900">
                            <i class="bi bi-stars text-xs shrink-0" aria-hidden="true"></i>
                            <p class="details">Nadia has a new insight for you</p>
                        </div>
                        <div class="relative flex-1 min-h-0 overflow-hidden">
                            <MarkdownText
                                :text="insightSummary"
                                class="body prose prose-slate max-w-none text-black prose-headings:my-0.5 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0" />
                            <div class="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent" aria-hidden="true"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="px-5 pb-5">
                <BaseButton theme="neutral" class="w-full" @click="handleClick">
                    {{ insightCount === 1 ? "See new insight" : `See ${insightCount ?? ""} new insights`.trim() }}
                </BaseButton>
            </div>
        </template>

        <!-- Activation state: checklist -->
        <template v-else-if="cardState === 'activation'">
            <div class="flex gap-5 p-5 flex-1">
                <img :src="InsightGradientBar" alt="" class="w-[9px] shrink-0 self-stretch rounded-full object-cover" />
                <div class="flex flex-col gap-3 flex-1">
                    <div class="nadia-subtitle text-[#292929]">Unlock My Work Insights</div>
                    <div class="text-[13px] leading-5 text-valence-grey-700 mt-0.5">Maximize Nadia's ability to generate quality, personalized insights each week</div>
                    <ul v-if="activationItems?.length" class="flex flex-col gap-2 mt-1 list-disc pl-5">
                        <li
                            v-for="item in activationItems"
                            :key="item.label"
                            class="body-small marker:text-valence-grey-400"
                            :class="item.complete ? 'text-valence-grey-400 line-through' : 'text-valence-grey-850'">
                            {{ item.label }}
                            <span class="sr-only">{{ item.complete ? "(completed)" : "(not completed)" }}</span>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="px-5 pb-5 mt-auto">
                <BaseButton theme="neutral" class="w-full" @click="handleClick">Unlock</BaseButton>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import InsightGradientBar from "~img/insight-gradient-bar.png";
import BaseButton from "~vue/components/BaseButton.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import type { MyWorkCardInfo } from "~vue/types/chat_home";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted } from "vue";

const props = defineProps<{
    cardState: "new_insights" | "activation";
    cardInfo?: MyWorkCardInfo | null;
}>();

const insightSummary = computed(() => (props.cardInfo?.kind === "new_insights" ? props.cardInfo.summary : null));
const insightCount = computed(() => (props.cardInfo?.kind === "new_insights" ? props.cardInfo.count : null));
const activationItems = computed(() => (props.cardInfo?.kind === "activation" ? props.cardInfo.items : null));

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CARD_SHOWN, {
        card_state: props.cardState,
        insight_count: insightCount.value ?? null,
    });
});

const handleClick = () => {
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CARD_CLICKED, {
        card_state: props.cardState,
        insight_count: insightCount.value ?? null,
    });
    router.visit("/intelligence/my-work/");
};
</script>
